// src/pages/championships/ChampionshipForm.jsx

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import championshipService from '../../services/championshipService';
import './ChampionshipForm.css';

function ChampionshipForm() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Estado para verificar se o usuário logado é Admin
  const [isAdmin, setIsAdmin] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    location: '', 
    venue: '',    
    gender: '',
    categoryType: 'none',
    baseCategory: '',
    customCategory: '',
    prizeFirst: '',
    prizeSecond: '',
    prizeThird: '',
    prizeFourth: '',
    format: 'Fase de grupos + Mata-mata (16 times)',
    knockoutPairingMethod: 'standard',
    startDate: '',
    logo: null,
    headerImage: null,
    
    // CONFIGURAÇÕES DE PARTIDA
    groupStageHasReturn: false,
    knockoutHasReturn: false,
    intergroupStage: false,
    
    // REGRAS DE SUSPENSÃO E MULTAS
    suspensionRules: {
      enabled: false,
      yellowCardsToSuspend: 3,
      redCardSuspensionGames: 1,
      finesEnabled: false,
      yellowCardFineValue: 0,
      redCardFineValue: 0
    },
    
    // CONFIGURAÇÃO CUSTOMIZADA EXPANDIDA
    customFormatConfig: {
      numberOfGroups: 4,
      teamsPerGroup: 4,
      teamsAdvancingPerGroup: 2,
      numberOfBestSecondPlaceToAdvance: 0,
      numberOfBestThirdPlaceToAdvance: 0,
      totalTeamsPointsCorridos: 10,
    },
    
    // TAXAS DE CREDENCIAMENTO
    enableTeamFee: false,
    teamRegistrationFee: 0,
    enableMemberFee: false,
    playerRegistrationFee: 0,
    coachRegistrationFee: 0,
    assistantCoachRegistrationFee: 0,
    enableTieredPlayerFee: false,
    playerLimitForNormalFee: 15,
    additionalPlayerFee: 0,
    registrationOpen: true,
    requireExistingPlayers: false,

    // SISTEMA DE CARTEIRINHA DIGITAL
    enableIdCard: false,
    idCardFee: 0,

    // TRANSIÇÃO DE DIVISÕES
    hasPromotionAndRelegation: false,
    allowPunishedEntities: true,

    // MERCADO DE TRANSFERÊNCIAS
    enablePlayerTransfers: false,
    transferFee: 0,

    // AUTO-INSCRIÇÃO DE ATLETAS
    allowPlayerSelfRegistration: true,
  });
  
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [headerImagePreview, setHeaderImagePreview] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Carregar nível de acesso do usuário (Admin)
  useEffect(() => {
    try {
      const userString = localStorage.getItem('user') || 
                         localStorage.getItem('@user') || 
                         localStorage.getItem('userData');
      if (userString) {
        const userData = JSON.parse(userString);
        if (userData?.role?.toLowerCase() === 'admin') {
          setIsAdmin(true);
        }
      }
    } catch (err) {
      console.error('Erro ao verificar permissão do usuário:', err);
    }
  }, []);

  // Cálculo do total de times por grupo no formato personalizado
  const teamsPerGroup = useMemo(() => {
    if (formData.format === 'Formato Personalizado') {
      return formData.customFormatConfig.teamsPerGroup;
    }
    return 0;
  }, [formData.format, formData.customFormatConfig.teamsPerGroup]);

  // Determinar se pode ter 2ºs lugares
  const canHaveSecondPlaces = useMemo(() => {
    const advancing = formData.customFormatConfig.teamsAdvancingPerGroup;
    return advancing === 1;
  }, [formData.customFormatConfig.teamsAdvancingPerGroup]);

  // Determinar se pode ter 3ºs lugares
  const canHaveThirdPlaces = useMemo(() => {
    const advancing = formData.customFormatConfig.teamsAdvancingPerGroup;
    if (advancing === 1) return true;
    if (advancing === 2 && teamsPerGroup >= 3) return true;
    return false;
  }, [formData.customFormatConfig.teamsAdvancingPerGroup, teamsPerGroup]);

  const maxSecondPlaces = useMemo(() => {
    if (!canHaveSecondPlaces) return 0;
    const totalGroups = formData.customFormatConfig.numberOfGroups;
    const advancingPerGroup = formData.customFormatConfig.teamsAdvancingPerGroup;
    return advancingPerGroup === 1 ? totalGroups : 0;
  }, [canHaveSecondPlaces, formData.customFormatConfig.numberOfGroups, formData.customFormatConfig.teamsAdvancingPerGroup]);

  const maxThirdPlaces = useMemo(() => {
    if (!canHaveThirdPlaces) return 0;
    return formData.customFormatConfig.numberOfGroups;
  }, [canHaveThirdPlaces, formData.customFormatConfig.numberOfGroups]);

  const totalKnockoutTeams = useMemo(() => {
    if (formData.format !== 'Formato Personalizado' && formData.format !== 'Grupo Único + Mata-mata') return 0;
    const config = formData.customFormatConfig;
    const groups = parseInt(config.numberOfGroups, 10) || 0;
    const direct = groups * (parseInt(config.teamsAdvancingPerGroup, 10) || 0);
    const bestSeconds = parseInt(config.numberOfBestSecondPlaceToAdvance, 10) || 0;
    const bestThirds = parseInt(config.numberOfBestThirdPlaceToAdvance, 10) || 0;
    return direct + bestSeconds + bestThirds;
  }, [formData.format, formData.customFormatConfig]);
  
  const isPowerOfTwo = (n) => n > 1 && (Math.log2(n) % 1) === 0;
  
  const isKnockoutConfigValid = useMemo(() => {
    if (formData.format === 'Pontos Corridos') return true;
    if (formData.format.includes('Mata-mata') && !formData.format.includes('Fase de grupos')) return true;
    if (formData.format === 'Formato Personalizado' || formData.format === 'Grupo Único + Mata-mata') {
      return totalKnockoutTeams > 0 && isPowerOfTwo(totalKnockoutTeams);
    }
    return true;
  }, [formData.format, totalKnockoutTeams]);

  const validateAndAdjustBestPlaces = (advancingPerGroup, currentConfig) => {
    const currentSecondPlaces = currentConfig.numberOfBestSecondPlaceToAdvance;
    const currentThirdPlaces = currentConfig.numberOfBestThirdPlaceToAdvance;
    let newSecondPlaces = currentSecondPlaces;
    let newThirdPlaces = currentThirdPlaces;
    
    if (advancingPerGroup === 1) {
      if (currentSecondPlaces > maxSecondPlaces) newSecondPlaces = maxSecondPlaces;
      if (currentThirdPlaces > maxThirdPlaces) newThirdPlaces = maxThirdPlaces;
    } else if (advancingPerGroup === 2) {
      newSecondPlaces = 0;
      if (currentThirdPlaces > maxThirdPlaces) newThirdPlaces = maxThirdPlaces;
    } else {
      newSecondPlaces = 0;
      newThirdPlaces = 0;
    }
    return { newSecondPlaces, newThirdPlaces };
  };

  useEffect(() => {
    if (id) {
      const fetchChampionship = async () => {
        setIsLoading(true);
        try {
          const response = await championshipService.getById(id);
          const champData = response.data;
          
          let categoryType = 'none';
          if (champData.baseCategory) categoryType = 'base';
          else if (champData.customCategory) categoryType = 'custom';

          const customFormatConfig = champData.customFormatConfig || {
            numberOfGroups: 4,
            teamsPerGroup: 4,
            teamsAdvancingPerGroup: 2,
            numberOfBestSecondPlaceToAdvance: 0,
            numberOfBestThirdPlaceToAdvance: 0,
            totalTeamsPointsCorridos: 10,
          };

          setFormData({
            ...formData,
            ...champData,
            startDate: champData.startDate ? champData.startDate.split('T')[0] : '',
            logo: null,
            headerImage: null,
            categoryType: categoryType,
            customFormatConfig: customFormatConfig,
            groupStageHasReturn: champData.groupStageHasReturn || false,
            knockoutHasReturn: champData.knockoutHasReturn || false,
            intergroupStage: champData.intergroupStage || false,
            enableIdCard: champData.enableIdCard || false,
            idCardFee: champData.idCardFee || 0,
            hasPromotionAndRelegation: champData.hasPromotionAndRelegation || false,
            requireExistingPlayers: champData.requireExistingPlayers !== undefined ? champData.requireExistingPlayers : false,
            allowPunishedEntities: champData.allowPunishedEntities !== undefined ? champData.allowPunishedEntities : true,
            enablePlayerTransfers: champData.enablePlayerTransfers || false,
            transferFee: champData.transferFee || 0,
            allowPlayerSelfRegistration: champData.allowPlayerSelfRegistration !== undefined ? champData.allowPlayerSelfRegistration : true,
            suspensionRules: {
              enabled: champData.suspensionRules?.enabled || false,
              yellowCardsToSuspend: champData.suspensionRules?.yellowCardsToSuspend || 3,
              redCardSuspensionGames: champData.suspensionRules?.redCardSuspensionGames || 1,
              finesEnabled: champData.suspensionRules?.finesEnabled || false,
              yellowCardFineValue: champData.suspensionRules?.yellowCardFineValue || 0,
              redCardFineValue: champData.suspensionRules?.redCardFineValue || 0,
            },
          });
          
          if (champData.logo) setLogoPreview(champData.logo);
          if (champData.headerImage) setHeaderImagePreview(champData.headerImage);
        } catch (error) {
          setError('Falha ao carregar dados do campeonato.');
          console.error('Erro ao carregar:', error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchChampionship();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => {
      const newState = { ...prev, [name]: newValue };
      if (name === 'categoryType') {
        newState.baseCategory = '';
        newState.customCategory = '';
      }
      return newState;
    });
  };
  
  const handleCustomConfigChange = (e) => {
    const { name, value } = e.target;
    const intValue = parseInt(value, 10) || 0;
    
    setFormData(prev => {
      const newConfig = { ...prev.customFormatConfig, [name]: intValue };
      if (name === 'teamsAdvancingPerGroup') {
        const { newSecondPlaces, newThirdPlaces } = validateAndAdjustBestPlaces(intValue, prev.customFormatConfig);
        newConfig.numberOfBestSecondPlaceToAdvance = newSecondPlaces;
        newConfig.numberOfBestThirdPlaceToAdvance = newThirdPlaces;
      }
      if (name === 'numberOfBestSecondPlaceToAdvance') {
        if (intValue > maxSecondPlaces) newConfig.numberOfBestSecondPlaceToAdvance = maxSecondPlaces;
      }
      if (name === 'numberOfBestThirdPlaceToAdvance') {
        if (intValue > maxThirdPlaces) newConfig.numberOfBestThirdPlaceToAdvance = maxThirdPlaces;
      }
      return { ...prev, customFormatConfig: newConfig };
    });
  };
  
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, logo: file }));
    if (file) {
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleHeaderImageChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, headerImage: file }));
    if (file) {
      setHeaderImagePreview(URL.createObjectURL(file));
    }
  };
  
  const handleSuspensionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      suspensionRules: {
        ...prev.suspensionRules,
        [name]: type === 'checkbox' 
          ? checked 
          : (name.includes('FineValue') ? parseFloat(value) || 0 : parseInt(value, 10) || 0),
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!isKnockoutConfigValid) {
      setError(`A soma de classificados (${totalKnockoutTeams}) deve resultar em um número de mata-mata válido (2, 4, 8, 16 ou 32).`);
      return;
    }
    
    setIsSubmitting(true);
    
    const dataToSend = { ...formData };
    
    // Tratamento de categorias
    if (dataToSend.categoryType === 'base') {
      dataToSend.customCategory = '';
    } else if (dataToSend.categoryType === 'custom') {
      dataToSend.baseCategory = '';
    } else {
      dataToSend.baseCategory = '';
      dataToSend.customCategory = '';
    }
    delete dataToSend.categoryType;

    if (dataToSend.format !== 'Formato Personalizado' && 
        dataToSend.format !== 'Grupo Único + Mata-mata' && 
        dataToSend.format !== 'Pontos Corridos') {
      delete dataToSend.customFormatConfig;
    }

    // Conversões numéricas
    dataToSend.teamRegistrationFee = parseFloat(dataToSend.teamRegistrationFee) || 0;
    dataToSend.playerRegistrationFee = parseFloat(dataToSend.playerRegistrationFee) || 0;
    dataToSend.coachRegistrationFee = parseFloat(dataToSend.coachRegistrationFee) || 0;
    dataToSend.assistantCoachRegistrationFee = parseFloat(dataToSend.assistantCoachRegistrationFee) || 0;
    dataToSend.additionalPlayerFee = parseFloat(dataToSend.additionalPlayerFee) || 0;
    dataToSend.playerLimitForNormalFee = parseInt(dataToSend.playerLimitForNormalFee, 10) || 15;
    dataToSend.idCardFee = parseFloat(dataToSend.idCardFee) || 0;
    dataToSend.transferFee = parseFloat(dataToSend.transferFee) || 0;
    
    // Montagem do FormData para envio de arquivos de forma idêntica ao Mobile
    const formDataToSend = new FormData();
    if (formData.logo instanceof File) {
      formDataToSend.append('logo', formData.logo);
    }
    if (formData.headerImage instanceof File) {
      formDataToSend.append('headerImage', formData.headerImage);
    }

    delete dataToSend.logo;
    delete dataToSend.headerImage;

    Object.keys(dataToSend).forEach(key => {
      const value = dataToSend[key];
      if (value !== undefined && value !== null) {
        if (typeof value === 'object' && !(value instanceof Date)) {
          formDataToSend.append(key, JSON.stringify(value));
        } else {
          formDataToSend.append(key, String(value));
        }
      }
    });
    
    try {
      if (id) {
        await championshipService.update(id, formDataToSend);
        navigate(`/championships/${id}`);
      } else {
        await championshipService.create(formDataToSend);
        navigate('/championships');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao salvar campeonato.';
      setError(errorMessage);
      console.error('Erro ao salvar:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSecondPlacesHint = () => {
    const advancing = formData.customFormatConfig.teamsAdvancingPerGroup;
    const totalGroups = formData.customFormatConfig.numberOfGroups;
    if (advancing === 1) return `✅ Quando apenas 1 time avança por grupo, você pode selecionar até ${totalGroups} melhores 2ºs lugares para completar o mata-mata.`;
    if (advancing === 2) return `ℹ️ Quando 2 times avançam por grupo (1º e 2º), não é necessário selecionar melhores 2ºs lugares.`;
    return '';
  };

  const getThirdPlacesHint = () => {
    const advancing = formData.customFormatConfig.teamsAdvancingPerGroup;
    const totalGroups = formData.customFormatConfig.numberOfGroups;
    if (advancing === 1) return `✅ Quando apenas 1 time avança por grupo, você pode selecionar até ${totalGroups} melhores 3ºs lugares para completar o mata-mata.`;
    if (advancing === 2) return `✅ Quando 2 times avançam por grupo (1º e 2º), você pode selecionar até ${totalGroups} melhores 3ºs lugares.`;
    return '';
  };

  if (isLoading) {
    return (
      <div className="form-container">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Carregando dados do campeonato...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <h1>{id ? 'Editar Campeonato' : 'Novo Campeonato'}</h1>
      
      {error && <div className="form-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <fieldset disabled={isSubmitting}>
          
          {/* INFORMAÇÕES BÁSICAS E IDENTIDADE VISUAL */}
          <fieldset>
            <legend>Informações Básicas e Identidade Visual</legend>
            
            <div className="form-grid">
              <div>
                <label>Nome do Campeonato *</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required />
              </div>
              <div>
                <label>Cidade *</label>
                <input type="text" name="location" value={formData.location} onChange={handleChange} required />
              </div>
              <div>
                <label>Local Padrão dos Jogos (Opcional)</label>
                <input type="text" name="venue" value={formData.venue} onChange={handleChange} placeholder="Ex: Ginásio Municipal" />
              </div>
              <div>
                <label>Data de Início *</label>
                <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} required />
              </div>
            </div>

            <div className="checkbox-group" style={{ marginTop: '15px' }}>
              <label className="checkbox-item">
                <input 
                  type="checkbox" 
                  name="registrationOpen" 
                  checked={formData.registrationOpen} 
                  onChange={handleChange} 
                />
                <strong>Inscrições abertas</strong>
              </label>
              <p className="form-hint">Desmarque para fechar as inscrições deste campeonato.</p>
            </div>

            {/* ✅ NOVO: Permissão de Auto-Inscrição de Jogadores */}
            <div className="checkbox-group" style={{ marginTop: '15px' }}>
              <label className="checkbox-item">
                <input 
                  type="checkbox" 
                  name="allowPlayerSelfRegistration" 
                  checked={formData.allowPlayerSelfRegistration} 
                  onChange={handleChange} 
                />
                <strong>Permitir Auto-Inscrição de Jogadores</strong>
              </label>
              <p className="form-hint">
                • <strong>Ativado:</strong> Os próprios atletas podem entrar na página do time e realizar sua auto-inscrição.<br/>
                • <strong>Desativado:</strong> Apenas a diretoria do time (Diretor, Técnico, Gestor) ou Admin podem adicionar jogadores.
              </p>
            </div>

            {/* OPÇÃO RESTRITA PARA ADMIN: Requer Jogadores Pré-Cadastrados */}
            {isAdmin && (
              <div className="checkbox-group" style={{ marginTop: '15px' }}>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    name="requireExistingPlayers" 
                    checked={formData.requireExistingPlayers} 
                    onChange={handleChange} 
                  />
                  <strong>Requer Jogadores Pré-Cadastrados <span style={{ color: '#2563eb' }}>(Admin)</span></strong>
                </label>
                <p className="form-hint">Se ativado, apenas jogadores já registrados na plataforma podem ser inscritos.</p>
              </div>
            )}

            <div style={{ marginTop: '15px' }}>
              <label>Logo do Campeonato</label>
              <div className="file-input">
                <input type="file" id="logo-upload" accept="image/*" onChange={handleLogoChange} />
                <label htmlFor="logo-upload">Escolher arquivo</label>
                <span>{formData.logo?.name || (logoPreview ? 'Arquivo atual mantido' : 'Nenhum arquivo')}</span>
              </div>
              {logoPreview && <img src={logoPreview} alt="Preview do Logo" className="form-preview-image" />}
            </div>
            
            <div style={{ marginTop: '15px' }}>
              <label>Imagem de Cabeçalho (Súmula/Ficha)</label>
              <div className="file-input">
                <input type="file" id="header-image-upload" accept="image/*" onChange={handleHeaderImageChange} />
                <label htmlFor="header-image-upload">Escolher arquivo</label>
                <span>{formData.headerImage?.name || (headerImagePreview ? 'Arquivo atual mantido' : 'Nenhum arquivo')}</span>
              </div>
              <p className="form-hint">Use uma imagem retangular (ex: 1200x300 pixels) para melhores resultados.</p>
              {headerImagePreview && <img src={headerImagePreview} alt="Preview do Cabeçalho" className="form-preview-image" />}
            </div>
          </fieldset>

          {/* CATEGORIA E GÊNERO */}
          <fieldset>
            <legend>Categoria e Gênero (Opcional)</legend>
            
            <div>
              <label>Gênero</label>
              <select name="gender" value={formData.gender} onChange={handleChange}>
                <option value="">Misto / Não Especificado</option>
                <option value="Masculino">Masculino</option>
                <option value="Feminino">Feminino</option>
              </select>
            </div>
            
            <div style={{ marginTop: '15px' }}>
              <label>Tipo de Categoria</label>
              <div className="radio-group">
                <label>
                  <input type="radio" name="categoryType" value="none" checked={formData.categoryType === 'none'} onChange={handleChange} /> 
                  Nenhuma
                </label>
                <label>
                  <input type="radio" name="categoryType" value="base" checked={formData.categoryType === 'base'} onChange={handleChange} /> 
                  Categoria de Base
                </label>
                <label>
                  <input type="radio" name="categoryType" value="custom" checked={formData.categoryType === 'custom'} onChange={handleChange} /> 
                  Outra
                </label>
              </div>
            </div>
            
            {formData.categoryType === 'base' && (
              <div style={{ marginTop: '15px' }}>
                <label>Selecione a Categoria de Base</label>
                <select name="baseCategory" value={formData.baseCategory} onChange={handleChange}>
                  <option value="">Selecione...</option>
                  <option value="Sub-11">Sub-11</option>
                  <option value="Sub-13">Sub-13</option>
                  <option value="Sub-15">Sub-15</option>
                  <option value="Sub-17">Sub-17</option>
                  <option value="Sub-20">Sub-20</option>
                </select>
              </div>
            )}
            
            {formData.categoryType === 'custom' && (
              <div style={{ marginTop: '15px' }}>
                <label>Digite a Categoria Personalizada</label>
                <input type="text" name="customCategory" value={formData.customCategory} onChange={handleChange} placeholder="Ex: Sênior, Master 40+" />
              </div>
            )}
          </fieldset>
          
          {/* PREMIAÇÃO */}
          <fieldset>
            <legend>Premiação (Opcional)</legend>
            <p className="form-hint">Descreva os prêmios para cada colocação.</p>
            <div className="prize-grid">
              <div>
                <label htmlFor="prizeFirst">1º Lugar</label>
                <input type="text" id="prizeFirst" name="prizeFirst" value={formData.prizeFirst} onChange={handleChange} />
              </div>
              <div>
                <label htmlFor="prizeSecond">2º Lugar</label>
                <input type="text" id="prizeSecond" name="prizeSecond" value={formData.prizeSecond} onChange={handleChange} />
              </div>
              <div>
                <label htmlFor="prizeThird">3º Lugar</label>
                <input type="text" id="prizeThird" name="prizeThird" value={formData.prizeThird} onChange={handleChange} />
              </div>
              <div>
                <label htmlFor="prizeFourth">4º Lugar</label>
                <input type="text" id="prizeFourth" name="prizeFourth" value={formData.prizeFourth} onChange={handleChange} />
              </div>
            </div>
          </fieldset>

          {/* ✅ NOVO: MERCADO DE TRANSFERÊNCIAS E EMPRÉSTIMOS (Apenas Admin) */}
          {isAdmin && (
            <fieldset>
              <legend>Mercado de Transferências e Empréstimos <span style={{ color: '#2563eb' }}>(Admin)</span></legend>
              <p className="form-hint">Permita que as equipes deste campeonato negociem, comprem ou peguem atletas emprestados de outros times durante a competição.</p>

              <div className="form-check">
                <input 
                  type="checkbox" 
                  id="enablePlayerTransfers" 
                  name="enablePlayerTransfers" 
                  checked={formData.enablePlayerTransfers} 
                  onChange={handleChange} 
                />
                <label htmlFor="enablePlayerTransfers">Permitir Transferências e Empréstimos de Atletas</label>
              </div>

              {formData.enablePlayerTransfers && (
                <div style={{ marginLeft: '20px', marginTop: '12px' }}>
                  <label htmlFor="transferFee">Taxa Administrativa por Transferência (R$)</label>
                  <input 
                    type="number" 
                    id="transferFee" 
                    name="transferFee" 
                    value={formData.transferFee} 
                    onChange={handleChange} 
                    min="0" 
                    step="0.01" 
                    placeholder="0,00 (Digite 0 para isento)" 
                  />
                  <p className="form-hint">Valor cobrado pelo campeonato via PIX para aprovar a movimentação do atleta de uma equipe para outra.</p>
                </div>
              )}
            </fieldset>
          )}

          {/* TAXAS DE CREDENCIAMENTO E SERVIÇOS (Apenas Admin) */}
          {isAdmin && (
            <fieldset>
              <legend>Taxas de Credenciamento e Serviços (PIX) <span style={{ color: '#2563eb' }}>(Admin)</span></legend>
              <p className="form-hint">Defina os valores financeiros e serviços digitais vinculados ao campeonato.</p>
              
              <div className="form-check">
                <input type="checkbox" id="enableTeamFee" name="enableTeamFee" checked={formData.enableTeamFee} onChange={handleChange} />
                <label htmlFor="enableTeamFee">Ativar taxa de Credenciamento por Time</label>
              </div>
              
              {formData.enableTeamFee && (
                <div style={{ marginLeft: '20px', marginBottom: '15px' }}>
                  <label htmlFor="teamRegistrationFee">Valor por Time (R$)</label>
                  <input type="number" id="teamRegistrationFee" name="teamRegistrationFee" value={formData.teamRegistrationFee} onChange={handleChange} min="0" step="0.01" />
                </div>
              )}
              
              <div className="form-check" style={{ marginTop: '20px' }}>
                <input type="checkbox" id="enableMemberFee" name="enableMemberFee" checked={formData.enableMemberFee} onChange={handleChange} />
                <label htmlFor="enableMemberFee">Ativar taxa de Credenciamento por Membro (Jogador/Técnico)</label>
              </div>
              
              {formData.enableMemberFee && (
                <div style={{ marginLeft: '20px', display: 'grid', gap: '15px' }}>
                  <div>
                    <label htmlFor="playerRegistrationFee">Valor por Jogador (R$)</label>
                    <input type="number" id="playerRegistrationFee" name="playerRegistrationFee" value={formData.playerRegistrationFee} onChange={handleChange} min="0" step="0.01" />
                  </div>
                  
                  <div className="form-check">
                    <input type="checkbox" id="enableTieredPlayerFee" name="enableTieredPlayerFee" checked={formData.enableTieredPlayerFee} onChange={handleChange} />
                    <label htmlFor="enableTieredPlayerFee">Ativar taxa de Credenciamento para jogador adicional?</label>
                  </div>

                  {formData.enableTieredPlayerFee && (
                    <div className="tiered-fee-group">
                      <div>
                        <label htmlFor="playerLimitForNormalFee">Nº de jogadores para taxa de Credenciamento normal</label>
                        <input type="number" id="playerLimitForNormalFee" name="playerLimitForNormalFee" value={formData.playerLimitForNormalFee} onChange={handleChange} min="0" step="1" />
                      </div>
                      <div>
                        <label htmlFor="additionalPlayerFee">Valor da taxa de Credenciamento adicional (R$)</label>
                        <input type="number" id="additionalPlayerFee" name="additionalPlayerFee" value={formData.additionalPlayerFee} onChange={handleChange} min="0" step="0.01" />
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="coachRegistrationFee">Valor por Técnico (R$)</label>
                    <input type="number" id="coachRegistrationFee" name="coachRegistrationFee" value={formData.coachRegistrationFee} onChange={handleChange} min="0" step="0.01" />
                  </div>
                  
                  <div>
                    <label htmlFor="assistantCoachRegistrationFee">Valor por Auxiliar Técnico (R$)</label>
                    <input type="number" id="assistantCoachRegistrationFee" name="assistantCoachRegistrationFee" value={formData.assistantCoachRegistrationFee} onChange={handleChange} min="0" step="0.01" />
                  </div>
                </div>
              )}

              <div className="form-check" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #f1f5f9' }}>
                <input 
                  type="checkbox" 
                  id="enableIdCard" 
                  name="enableIdCard" 
                  checked={formData.enableIdCard} 
                  onChange={handleChange} 
                />
                <label htmlFor="enableIdCard">Ativar sistema de <strong>Carteirinha Digital</strong> para atletas</label>
              </div>
              
              {formData.enableIdCard && (
                <div style={{ marginLeft: '20px', marginTop: '10px' }}>
                  <label htmlFor="idCardFee">Taxa de Emissão de Carteirinha (R$)</label>
                  <input 
                    type="number" 
                    id="idCardFee" 
                    name="idCardFee" 
                    value={formData.idCardFee} 
                    onChange={handleChange} 
                    min="0" 
                    step="0.01" 
                  />
                  <p className="form-hint">Insira 0 para permitir que a carteirinha seja gratuita.</p>
                </div>
              )}
            </fieldset>
          )}
          
          {/* FORMATO E REGRAS */}
          <fieldset>
            <legend>Formato e Regras</legend>
            
            <div>
              <label>Formato *</label>
              <select name="format" value={formData.format} onChange={handleChange} required>
                <optgroup label="Padrão">
                  <option value="Mata-mata (4 times)">Mata-mata (4 times)</option>
                  <option value="Mata-mata (8 times)">Mata-mata (8 times)</option>
                  <option value="Mata-mata (16 times)">Mata-mata (16 times)</option>
                  <option value="Fase de grupos + Mata-mata (16 times)">Fase de grupos + Mata-mata (16 times)</option>
                  <option value="Fase de grupos + Mata-mata (32 times)">Fase de grupos + Mata-mata (32 times)</option>
                </optgroup>
                <optgroup label="Flexível">
                  <option value="Pontos Corridos">Pontos Corridos (Liga)</option>
                  <option value="Grupo Único + Mata-mata">Grupo Único + Mata-mata</option>
                  <option value="Formato Personalizado">Personalizado (Múltiplos Grupos)</option>
                </optgroup>
              </select>
            </div>

            <div className="return-matches-config" style={{ marginTop: '15px', marginBottom: '15px' }}>
              <label className="checkbox-item" style={{ display: 'block', marginBottom: '8px' }}>
                <input 
                  type="checkbox" 
                  name="groupStageHasReturn" 
                  checked={formData.groupStageHasReturn} 
                  onChange={handleChange} 
                />
                Fase de Grupos/Liga terá <strong>Ida e Volta</strong>?
              </label>
              
              {formData.format.includes('Mata-mata') && (
                <label className="checkbox-item" style={{ display: 'block', marginBottom: '8px' }}>
                  <input 
                    type="checkbox" 
                    name="knockoutHasReturn" 
                    checked={formData.knockoutHasReturn} 
                    onChange={handleChange} 
                  />
                  Mata-mata terá <strong>Ida e Volta</strong>? (exceto final)
                </label>
              )}

              {(formData.format.includes('Fase de grupos') || 
                (formData.format === 'Formato Personalizado' && formData.customFormatConfig.numberOfGroups > 1)) && (
                <label className="checkbox-item" style={{ display: 'block', marginBottom: '8px' }}>
                  <input 
                    type="checkbox" 
                    name="intergroupStage" 
                    checked={formData.intergroupStage} 
                    onChange={handleChange} 
                  />
                  Ativar <strong>Confrontos Intergrupos</strong>? (Equipes de chaves diferentes se enfrentam)
                </label>
              )}

              {/* Promoção e Rebaixamento restrito a Admin */}
              {isAdmin && (
                <div style={{ marginTop: '10px' }}>
                  <label className="checkbox-item" style={{ display: 'block' }}>
                    <input 
                      type="checkbox" 
                      name="hasPromotionAndRelegation" 
                      checked={formData.hasPromotionAndRelegation} 
                      onChange={handleChange} 
                    />
                    Habilitar <strong>Promoção e Rebaixamento</strong> <span style={{ color: '#2563eb' }}>(Admin)</span>
                  </label>
                  <p className="form-hint">Permite criar transição de elencos com outros campeonatos vinculados ao fim do torneio.</p>
                </div>
              )}
            </div>

            {/* Configuração para formatos personalizados */}
            {(formData.format === 'Formato Personalizado' || formData.format === 'Grupo Único + Mata-mata' || formData.format === 'Pontos Corridos') && (
              <div className="custom-box" style={{ marginTop: '15px', padding: '15px', border: '1px solid #ddd', borderRadius: '4px' }}>
                
                {formData.format !== 'Pontos Corridos' && (
                  <>
                    <div className="form-grid">
                      {formData.format === 'Formato Personalizado' && (
                        <div>
                          <label>Número de Grupos</label>
                          <input 
                            type="number" 
                            name="numberOfGroups" 
                            value={formData.customFormatConfig.numberOfGroups} 
                            onChange={handleCustomConfigChange} 
                            min="1" 
                          />
                        </div>
                      )}
                      
                      {formData.format !== 'Grupo Único + Mata-mata' && (
                        <div>
                          <label>Times por Grupo</label>
                          <input 
                            type="number" 
                            name="teamsPerGroup" 
                            value={formData.customFormatConfig.teamsPerGroup} 
                            onChange={handleCustomConfigChange} 
                            min="2" 
                          />
                        </div>
                      )}
                      
                      <div>
                        <label>Times que Avançam por Grupo (Direto)</label>
                        <input 
                          type="number" 
                          name="teamsAdvancingPerGroup" 
                          value={formData.customFormatConfig.teamsAdvancingPerGroup} 
                          onChange={handleCustomConfigChange} 
                          min="1" 
                          max={formData.format === 'Grupo Único + Mata-mata' ? 100 : formData.customFormatConfig.teamsPerGroup}
                        />
                      </div>
                    </div>

                    <div className="form-grid" style={{ marginTop: '10px' }}>
                      <div>
                        <label>Melhores 2ºs Colocados (Extras)</label>
                        <input 
                          type="number" 
                          name="numberOfBestSecondPlaceToAdvance" 
                          value={formData.customFormatConfig.numberOfBestSecondPlaceToAdvance} 
                          onChange={handleCustomConfigChange} 
                          min="0" 
                          max={maxSecondPlaces}
                          disabled={!canHaveSecondPlaces}
                          style={{
                            backgroundColor: !canHaveSecondPlaces ? '#f0f0f0' : 'white',
                            cursor: !canHaveSecondPlaces ? 'not-allowed' : 'text'
                          }}
                        />
                        <p className="form-hint">{getSecondPlacesHint()}</p>
                      </div>
                      <div>
                        <label>Melhores 3ºs Colocados (Extras)</label>
                        <input 
                          type="number" 
                          name="numberOfBestThirdPlaceToAdvance" 
                          value={formData.customFormatConfig.numberOfBestThirdPlaceToAdvance} 
                          onChange={handleCustomConfigChange} 
                          min="0" 
                          max={maxThirdPlaces}
                          disabled={!canHaveThirdPlaces}
                          style={{
                            backgroundColor: !canHaveThirdPlaces ? '#f0f0f0' : 'white',
                            cursor: !canHaveThirdPlaces ? 'not-allowed' : 'text'
                          }}
                        />
                        <p className="form-hint">{getThirdPlacesHint()}</p>
                      </div>
                    </div>
                  </>
                )}

                {formData.format === 'Pontos Corridos' && (
                  <div>
                    <label>Número Total de Times</label>
                    <input 
                      type="number" 
                      name="totalTeamsPointsCorridos" 
                      value={formData.customFormatConfig.totalTeamsPointsCorridos} 
                      onChange={handleCustomConfigChange} 
                      min="2" 
                      max="100"
                    />
                  </div>
                )}

                <div className="summary-info" style={{ marginTop: '15px', padding: '10px', background: '#f5f5f5', borderRadius: '4px' }}>
                  {formData.format !== 'Pontos Corridos' ? (
                    <p>
                      Total de classificados para o Mata-mata: <strong>{totalKnockoutTeams}</strong>
                      {isKnockoutConfigValid ? ' ✅ Válido' : ' ❌ Deve ser 2, 4, 8, 16...'}
                    </p>
                  ) : (
                    <p>O campeonato será decidido por pontos acumulados entre todos os times.</p>
                  )}
                </div>
              </div>
            )}

            {(formData.format.toLowerCase().includes('mata-mata') || formData.format === 'Formato Personalizado' || formData.format === 'Grupo Único + Mata-mata') && 
             formData.format !== 'Pontos Corridos' && (
              <div style={{ marginTop: '15px' }}>
                <label>Método de Chaveamento do Mata-Mata *</label>
                <select name="knockoutPairingMethod" value={formData.knockoutPairingMethod} onChange={handleChange} required>
                  <option value="standard">Padrão (Ex: 1ºA vs 2ºB)</option>
                  <option value="random">Sorteio Aleatório</option>
                  <option value="manual">Manual</option>
                </select>
              </div>
            )}
          </fieldset>
          
          {/* REGRAS DE SUSPENSÃO E MULTAS */}
          <fieldset>
            <legend>Regras de Suspensão Automática e Multas</legend>
            
            <div className="form-check">
              <input 
                type="checkbox" 
                id="suspensionEnabled" 
                name="enabled" 
                checked={formData.suspensionRules.enabled} 
                onChange={handleSuspensionChange} 
              />
              <label htmlFor="suspensionEnabled">Ativar suspensão por cartões</label>
            </div>
            
            {formData.suspensionRules.enabled && (
              <div className="suspension-details" style={{ marginTop: '10px', marginLeft: '20px' }}>
                <div>
                  <label htmlFor="yellowCardsToSuspend">Nº de amarelos para suspender</label>
                  <input 
                    type="number" 
                    id="yellowCardsToSuspend" 
                    name="yellowCardsToSuspend" 
                    value={formData.suspensionRules.yellowCardsToSuspend} 
                    onChange={handleSuspensionChange} 
                    min="1" 
                  />
                </div>
                <div style={{ marginTop: '10px' }}>
                  <label htmlFor="redCardSuspensionGames">Jogos de suspensão por vermelho</label>
                  <input 
                    type="number" 
                    id="redCardSuspensionGames" 
                    name="redCardSuspensionGames" 
                    value={formData.suspensionRules.redCardSuspensionGames} 
                    onChange={handleSuspensionChange} 
                    min="1" 
                  />
                </div>
              </div>
            )}

            {/* ✅ MULTAS FINANCEIRAS POR SUSPENSÃO (Apenas Admin) */}
            {isAdmin && (
              <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e2e8f0' }}>
                <div className="form-check">
                  <input 
                    type="checkbox" 
                    id="finesEnabled" 
                    name="finesEnabled" 
                    checked={formData.suspensionRules.finesEnabled} 
                    onChange={handleSuspensionChange} 
                  />
                  <label htmlFor="finesEnabled">
                    Ativar multas financeiras por suspensão <span style={{ color: '#2563eb' }}>(Admin)</span>
                  </label>
                </div>

                {formData.suspensionRules.finesEnabled && (
                  <div style={{ marginLeft: '20px', marginTop: '10px', display: 'grid', gap: '10px' }}>
                    <div>
                      <label htmlFor="yellowCardFineValue">Valor da multa por Acúmulo de Amarelos (R$)</label>
                      <input 
                        type="number" 
                        id="yellowCardFineValue" 
                        name="yellowCardFineValue" 
                        value={formData.suspensionRules.yellowCardFineValue} 
                        onChange={handleSuspensionChange} 
                        min="0" 
                        step="0.01" 
                        placeholder="0,00" 
                      />
                    </div>
                    <div>
                      <label htmlFor="redCardFineValue">Valor da multa por Cartão Vermelho (R$)</label>
                      <input 
                        type="number" 
                        id="redCardFineValue" 
                        name="redCardFineValue" 
                        value={formData.suspensionRules.redCardFineValue} 
                        onChange={handleSuspensionChange} 
                        min="0" 
                        step="0.01" 
                        placeholder="0,00" 
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </fieldset>

          {/* CRITÉRIO DE DISCIPLINA (Apenas Admin) */}
          {isAdmin && (
            <div className="penalty-selector-container" style={{
                backgroundColor: formData.allowPunishedEntities ? '#fff5f5' : '#f8fafc',
                padding: '20px',
                borderRadius: '12px',
                border: formData.allowPunishedEntities ? '1px solid #feb2b2' : '1px solid #e2e8f0',
                marginTop: '20px',
                marginBottom: '20px'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ flex: 1, marginRight: '20px' }}>
                        <h3 style={{ 
                            margin: 0, 
                            fontSize: '16px', 
                            fontWeight: 'bold', 
                            color: formData.allowPunishedEntities ? '#c53030' : '#1e293b' 
                        }}>
                            Critério de Disciplina <span style={{ color: '#2563eb' }}>(Admin)</span>
                        </h3>
                        <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#64748b' }}>
                            Permitir que atletas e times com <strong>suspensão global ativa</strong> participem deste campeonato?
                        </p>
                    </div>

                    <label className="switch" style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: '60px',
                        height: '34px'
                    }}>
                        <input 
                            type="checkbox" 
                            checked={formData.allowPunishedEntities}
                            onChange={(e) => setFormData({ ...formData, allowPunishedEntities: e.target.checked })}
                            style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span className="slider" style={{
                            position: 'absolute',
                            cursor: 'pointer',
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: formData.allowPunishedEntities ? '#f56565' : '#ccc',
                            transition: '.4s',
                            borderRadius: '34px'
                        }}>
                            <span style={{
                                position: 'absolute',
                                content: '""',
                                height: '26px', width: '26px',
                                left: formData.allowPunishedEntities ? '30px' : '4px',
                                bottom: '4px',
                                backgroundColor: 'white',
                                transition: '.4s',
                                borderRadius: '50%'
                            }}></span>
                        </span>
                    </label>
                </div>

                <div style={{ marginTop: '12px' }}>
                    {formData.allowPunishedEntities ? (
                        <div style={{ color: '#c53030', fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                            <span style={{ marginRight: '5px' }}>⚠️</span> 
                            <strong>Atenção:</strong> Atletas banidos pelo Tribunal do sistema PODERÃO ser inscritos normalmente.
                        </div>
                    ) : (
                        <div style={{ color: '#2f855a', fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                            <span style={{ marginRight: '5px' }}>✅</span> 
                            Inscrições de atletas/times suspensos serão <strong>bloqueadas automaticamente</strong> pelo sistema.
                        </div>
                    )}
                </div>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn-save" disabled={isSubmitting || !isKnockoutConfigValid}>
              {isSubmitting ? (id ? 'Atualizando...' : 'Salvando...') : (id ? 'Atualizar Campeonato' : 'Criar Campeonato')}
            </button>
            <button type="button" className="btn-back" onClick={() => navigate(id ? `/championships/${id}` : '/championships')}>
              Cancelar
            </button>
          </div>

        </fieldset>
      </form>
    </div>
  );
}

export default ChampionshipForm;